import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { plansData, APP_URL } from "@/data/mock";
import { SEO, PAGE_SEO } from "@/components/SEO";

export default function Upgrade() {
  const upgradePlans = plansData.filter((p) => p.name !== "Lifely Insights");

  return (
    <div className="pt-24">
      <SEO {...PAGE_SEO.upgrade} />
      <section className="py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4">
              Upgrade
            </p>
            <h1 className="font-serif text-4xl md:text-5xl text-foreground">
              Upgrade your plan
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Go beyond insights. Map your important information, set continuity
              check-ins, and keep everything in context.
            </p>
          </motion.div>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {upgradePlans.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 + 0.3, duration: 0.6 }}
              >
                <Card
                  className={`h-full ${
                    plan.highlighted
                      ? "border-primary/25 shadow-lg ring-1 ring-primary/10"
                      : "border-border/60"
                  } bg-card hover:shadow-md transition-shadow duration-300`}
                >
                  <CardContent className="p-8 flex flex-col h-full">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-foreground">
                        {plan.name}
                      </h3>
                      {plan.highlighted && (
                        <span className="text-[0.65rem] uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                          Popular
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {plan.tagline}
                    </p>
                    <div className="mt-6 mb-6">
                      <span className="text-4xl font-serif text-foreground">
                        {plan.price}
                      </span>
                      {plan.priceNote && (
                        <span className="text-sm text-muted-foreground ml-1.5">
                          {plan.priceNote}
                        </span>
                      )}
                    </div>
                    <ul className="space-y-3.5 mb-8">
                      {plan.features.map((feature, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <Check className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                          <span className="text-sm text-foreground/80">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto">
                      <Button
                        onClick={() =>
                          !plan.comingSoon && window.open(APP_URL, "_blank")
                        }
                        disabled={plan.comingSoon}
                        className={`w-full rounded-full h-11 ${
                          plan.highlighted
                            ? "bg-foreground text-background hover:bg-foreground/85"
                            : plan.comingSoon
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        variant={plan.highlighted ? "default" : "outline"}
                      >
                        {plan.cta}
                        {!plan.comingSoon && (
                          <ArrowUpRight className="w-4 h-4 ml-1.5" />
                        )}
                      </Button>
                      {plan.note && (
                        <p className="text-xs text-muted-foreground mt-3 text-center">
                          {plan.note}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-10 text-center"
          >
            <Link
              to="/pricing"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              View all plans & FAQ \u2192
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
